import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Link from "@mui/material/Link"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import * as getCaretCoordinates from "textarea-caret"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import {
  MULTIPART_HEADERS,
  useAuthorizedAxiosClient,
} from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import LengthLimitedTextField from "../../../common/components/LengthLimitedTextField"
import Row from "../../../common/components/Row"
import SegmentTracker from "../../../common/tracking"
import { selectCRMSettings } from "../reducer"
import ConfirmDeleteSMSCampaignDialog from "../sms-campaigns/update/ConfirmDeleteSMSCampaignDialog"
import { CAMPAIGN_OPERATIONS } from "../utils"
import { AUTOCOMPLETE_OPTIONS, useDialogContainer } from "../utils"
import AIGenButton, { useAIGenButtonState } from "./AIGenButton"
import AddAttachmentsSection, {
  useAddAttachmentSectionState,
} from "./AddAttachmentsSection"
import BrokerInfoTooltip from "./BrokerInfoTooltip"
import DialogHeader from "./DialogHeader"
import GenerateEmailOrSMSCampaignPromptPopover from "./GenerateEmailOrSMSCampaignPromptPopover"

const BRACKETS_REGEX = new RegExp("{{\\s*$")

export default function UpdateCreateSMSCampaignDialog({
  open,
  onClose,
  campaign,
  renderedText,
  onError,
}) {
  const [name, setName] = useState("")
  const { aiGenerateButtonRef, setAIGenerateButtonRef } = useAIGenButtonState()
  const [text, setText] = useState("")
  const textInputRef = useRef()
  const [autoCompleteLinkRef, setAutoCompleteLinkRef] = useState(null)
  const [autoCompleteMenuPos, setAutoCompleteMenuPos] = useState(null)
  const {
    fileAttachments,
    setFileAttachments,
    fileAttachmentError,
    setFileAttachmentError,
    setDeletedAttachments,
    addAttachmentsToFormData,
    convertBackendAttachmentsToFileAttachments,
  } = useAddAttachmentSectionState()
  const [isCreating, setIsCreating] = useState(false)
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false)
  const crmSettings = useSelector(selectCRMSettings)
  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()

  function handleTextChanged(e) {
    setText(e.target.value)
    const localCaretPos = getCaretCoordinates(e.target, e.target.selectionEnd)
    const fieldBoundingRect = e.target.getBoundingClientRect()
    if (
      BRACKETS_REGEX.test(e.target.value.substring(0, e.target.selectionEnd))
    ) {
      setAutoCompleteMenuPos({
        top: localCaretPos.top + fieldBoundingRect.top,
        left: localCaretPos.left + fieldBoundingRect.left,
      })
    } else {
      setAutoCompleteMenuPos(null)
    }
  }

  function handleGenerateSMSCampaignPromptPopoverClosed(generatedCampaign) {
    setAIGenerateButtonRef(null)
    if (generatedCampaign) {
      setName(generatedCampaign.title)
      setText(generatedCampaign.content)
    }
  }

  function handleAutoCompleteOptionSelected(autoCompleteText) {
    return () => {
      handleAutoCompleteMenuClosed()
      const selectionEnd = textInputRef.current.selectionEnd
      const startBrackets = autoCompleteLinkRef ? "{{" : ""
      setText(
        text.substring(0, selectionEnd) +
          `${startBrackets} ${autoCompleteText} }}` +
          text.substring(selectionEnd, text.length)
      )
    }
  }

  function handleAutoCompleteMenuClosed() {
    setAutoCompleteMenuPos(null)
    setAutoCompleteLinkRef(null)
  }

  function handleAutocompleteLinkClicked(e) {
    e.preventDefault()
    setAutoCompleteLinkRef(e.currentTarget)
  }

  function createCampaign(callback = () => {}) {
    return callBackend(
      getCreatePromise,
      CAMPAIGN_OPERATIONS.created,
      callback
    ).then(() =>
      SegmentTracker.track("SMS template created", { Name: name, Text: text })
    )
  }

  function updateCampaign(callback = () => {}, campaign) {
    return callBackend(
      getUpdatePromise,
      CAMPAIGN_OPERATIONS.updated,
      callback,
      campaign
    )
  }

  function getSMSCampaignAPIData() {
    const formData = new FormData()
    formData.append("name", name)
    formData.append("text", text)
    addAttachmentsToFormData(formData)
    return formData
  }

  function getCreatePromise() {
    return axios.post(
      "/workflows/api/sms/",
      getSMSCampaignAPIData(),
      MULTIPART_HEADERS
    )
  }

  function getUpdatePromise(campaign) {
    return axios.put(
      `/workflows/api/sms/${campaign.id}/`,
      getSMSCampaignAPIData(),
      MULTIPART_HEADERS
    )
  }

  function callBackend(getPromiseFunc, operationType, callback, campaign) {
    setIsCreating(true)
    return getPromiseFunc(campaign)
      .then(({ data }) => {
        callback(data, operationType)
      })
      .catch((error) => {
        const message = parseApiErrorMessage(
          error.response?.data,
          "Could not save SMS template"
        )
        onError?.(message)
        throw error
      })
      .finally(() => setIsCreating(false))
  }

  function handleSoftClose() {
    onClose()
  }

  function handleUpdateCreateButtonClicked() {
    const actionFunc = campaign ? updateCampaign : createCampaign
    actionFunc(onClose, campaign)
  }

  function handleConfirmDeleteSMSDialogClosed(deleted) {
    if (deleted) {
      onClose(campaign, CAMPAIGN_OPERATIONS.deleted)
    }
    setConfirmDeleteDialogOpen(false)
  }

  function updateFields(name, text, attachments) {
    setName(name)
    setText(text)
    convertBackendAttachmentsToFileAttachments(attachments)
  }

  useEffect(
    () => {
      if (campaign) {
        updateFields(campaign.name, campaign.text, campaign.attachments)
      } else {
        updateFields("", "", [])
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaign]
  )

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      maxWidth="sm"
      fullWidth
    >
      <DialogHeader
        title={`${campaign ? "Edit" : "Create"} SMS Template`}
        onClose={handleSoftClose}
      />
      <ConditionalPurpleLinearProgress visible={crmSettings.loading} />
      <DialogContent>
        <Column sx={{ gap: 2 }}>
          <BrokerInfoTooltip />
          <Row sx={{ gap: 1 }}>
            <LengthLimitedTextField
              label="Template name"
              value={name}
              setValue={setName}
              maxLength={128}
              required
            />
            <AIGenButton
              campaign={campaign}
              setAIGenerateButtonRef={setAIGenerateButtonRef}
            />
            <GenerateEmailOrSMSCampaignPromptPopover
              anchorElem={aiGenerateButtonRef}
              onClose={handleGenerateSMSCampaignPromptPopoverClosed}
              apiType="sms"
            />
          </Row>
          <Row sx={{ alignItems: "center", gap: 1, mb: 1 }}>
            <InfoOutlinedIcon />
            <Typography variant="body1">
              Type double opening curly braces <CodeText>&#123;&#123;</CodeText>{" "}
              to autocomplete agent data or just{" "}
              <Link
                href="#"
                color="primary.main"
                onClick={handleAutocompleteLinkClicked}
              >
                click here
              </Link>
              .
            </Typography>
          </Row>
          <TextField
            label="Text"
            value={text}
            onChange={handleTextChanged}
            multiline
            rows={5}
            required
            inputRef={textInputRef}
          />
          <Typography
            variant="body"
            color={
              text.length <= 140
                ? "text.secondary"
                : text.length <= 160
                ? "warning.main"
                : "error.main"
            }
            sx={{ alignSelf: "flex-end" }}
          >
            {text.length} characters
          </Typography>
          <Menu
            open={!!autoCompleteMenuPos || !!autoCompleteLinkRef}
            anchorEl={autoCompleteLinkRef}
            onClose={handleAutoCompleteMenuClosed}
            anchorReference={
              autoCompleteLinkRef ? "anchorEl" : "anchorPosition"
            }
            anchorPosition={autoCompleteMenuPos}
            sx={{ maxHeight: "320px" }}
          >
            {AUTOCOMPLETE_OPTIONS.map((option, index) => (
              <MenuItem
                key={index}
                onClick={handleAutoCompleteOptionSelected(option.fieldName)}
              >
                <ListItemText
                  primary={option.label}
                  secondary={`{{ ${option.fieldName} }}`}
                />
              </MenuItem>
            ))}
          </Menu>
        </Column>
        {renderedText ? (
          <Accordion
            defaultExpanded={false}
            sx={{ "&.MuiPaper-root": { width: "100%" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h5">Actual text sent</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" whiteSpace="pre-wrap">
                {renderedText}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ) : null}
        <AddAttachmentsSection
          fileAttachments={fileAttachments}
          setFileAttachments={setFileAttachments}
          fileAttachmentError={fileAttachmentError}
          setFileAttachmentError={setFileAttachmentError}
          setDeletedAttachments={setDeletedAttachments}
        />
      </DialogContent>
      <DialogActions
        sx={{ justifyContent: campaign ? "space-between" : "flex-end" }}
      >
        {campaign ? (
          <Button
            variant="contained"
            color="error"
            onClick={() => setConfirmDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        ) : null}
        <Row gap={1}>
          <Button variant="outlined" color="inherit" onClick={handleSoftClose}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={isCreating}
            disabled={!name || !text}
            onClick={handleUpdateCreateButtonClicked}
          >
            {campaign ? "Save" : "Create"}
          </LoadingButton>
        </Row>
      </DialogActions>
      {campaign ? (
        <ConfirmDeleteSMSCampaignDialog
          open={confirmDeleteDialogOpen}
          onClose={handleConfirmDeleteSMSDialogClosed}
          smsCampaign={campaign}
        />
      ) : null}
    </Container>
  )
}

export function useCreateSMSCampaignDialogState() {
  const [createSMSCampaignDialogOpen, setCreateSMSCampaignDialogOpen] =
    useState(false)

  function handleCreateCampaignDialogClosed(
    setAvailableCampaigns,
    onCampaignCreated = () => {}
  ) {
    return (createdCampaign) => {
      if (createdCampaign) {
        setAvailableCampaigns((availableCampaigns) =>
          availableCampaigns.concat(createdCampaign)
        )
        onCampaignCreated(createdCampaign)
      }
      setCreateSMSCampaignDialogOpen(false)
    }
  }

  return {
    createSMSCampaignDialogOpen,
    setCreateSMSCampaignDialogOpen,
    handleCreateCampaignDialogClosed,
  }
}

const CodeText = styled((props) => <Typography component="span" {...props} />)(
  ({ theme }) => ({
    background: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: "4px",
    padding: "2px 4px",
  })
)
